import { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import {
    clearFacetValues,
    selectFacetValue
} from "@/redux/modules/value/search/search.actions";
import { getSelectedValuesForFacet } from "@/redux/modules/value/search/search.selectors";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";

import { FacetComponentProps } from "../../@types/facet.types";
import { DefaultFacetValue, FacetType } from "../../@types/facetType.types";
import {
    FACET_DELIMITER,
    FACET_SORT_BY,
    FACET_VALUE_STORE_STOCK_IDENTIFIER,
    getOperator
} from "../../facet.configuration";
import { ShowProductsFacetComponent } from "./showProductsFacet.component";
import { ShowProductsFacetValues } from "./showProductsFacet.types";

const ShowProductsFacet = ({
    facet,
    component,
    sendEvent
}: FacetComponentProps<FacetType.SHOW_PRODUCTS>) => {
    const dispatch = useThunkDispatch();

    const selectedValues = useSelector(
        getSelectedValuesForFacet(component, facet.name),
        shallowEqual
    );

    const operator = useMemo(() => getOperator(facet.name), [facet.name]);
    const invalidations = useMemo(
        () => getInvalidationsForComponent(component),
        [component]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const createOnRefine = useCallback(
        (refined: boolean, facetName: string, facetValue: string) => () =>
            dispatch(
                refined
                    ? clearFacetValues(
                          component,
                          facetName,
                          [facetValue],
                          invalidations
                      )
                    : selectFacetValue(
                          component,
                          {
                              name: facetName,
                              operator: operator
                          },
                          facetValue,
                          invalidations,
                          sendEvent
                      )
            ),
        [component, operator, invalidations, dispatch]
    );

    const selectedValuesObj = selectedValues.reduce((acc, curr) => {
        acc[curr] = 0;
        return acc;
    }, {} as DefaultFacetValue);

    const facetValues = Object.entries({
        ...selectedValuesObj,
        ...facet.values
    })
        .map(([facetValueName, count]) => {
            const refined = selectedValues.includes(facetValueName);
            return {
                label: facetValueName,
                count: count,
                refined: refined,
                onRefine: createOnRefine(refined, facet.name, facetValueName)
            };
        })
        .sort(FACET_SORT_BY)
        .reduce<ShowProductsFacetValues>(
            (splittedValues, value) => {
                const [valueIdentifier, ...valueProperties] =
                    value.label.split(FACET_DELIMITER);

                if (valueIdentifier === FACET_VALUE_STORE_STOCK_IDENTIFIER) {
                    const [storeId] = valueProperties;

                    if (storeId) {
                        splittedValues.storeValues.push({
                            ...value,
                            storeCode: storeId
                        });
                    }
                } else {
                    splittedValues.defaultValues.push(value);
                }

                return splittedValues;
            },
            {
                defaultValues: [],
                storeValues: []
            }
        );

    return <ShowProductsFacetComponent values={facetValues} />;
};

export { ShowProductsFacet };
